/**
 * Reducer pour la gestion d'état du traitement de paie
 * Gère toutes les mutations d'état de manière prévisible
 */

import {
  PayrollPeriod,
  PayrollBatch,
  PayrollVariable,
  PayrollCalculation,
  PayrollEmployee,
  PayrollStatistics,
  PayrollError,
  PayrollProcessingConfig,
} from "../../../types/hr";

// ===== ÉTAT =====

export interface PayrollState {
  // Données
  periods: PayrollPeriod[];
  currentPeriod: PayrollPeriod | null;
  currentBatch: PayrollBatch | null;
  employees: PayrollEmployee[];
  variables: PayrollVariable[];
  calculations: PayrollCalculation[];
  statistics: PayrollStatistics | null;

  // États de chargement
  loading: {
    periods: boolean;
    employees: boolean;
    variables: boolean;
    calculations: boolean;
    batch: boolean;
    statistics: boolean;
  };

  // Erreurs
  errors: PayrollError[];

  // Configuration
  config: PayrollProcessingConfig;
}

// ===== ACTIONS =====

export type PayrollAction =
  // Actions de chargement
  | {
      type: "SET_LOADING";
      resource: keyof PayrollState["loading"];
      loading: boolean;
    }

  // Actions de périodes
  | { type: "SET_PERIODS"; periods: PayrollPeriod[] }
  | { type: "ADD_PERIOD"; period: PayrollPeriod }
  | { type: "UPDATE_PERIOD"; id: number; period: Partial<PayrollPeriod> }
  | { type: "REMOVE_PERIOD"; id: number }
  | { type: "SET_CURRENT_PERIOD"; period: PayrollPeriod | null }

  // Actions d'employés
  | { type: "SET_EMPLOYEES"; employees: PayrollEmployee[] }
  | { type: "ADD_EMPLOYEE"; employee: PayrollEmployee }
  | { type: "UPDATE_EMPLOYEE"; id: number; employee: Partial<PayrollEmployee> }
  | { type: "REMOVE_EMPLOYEE"; employeeId: number }

  // Actions de variables
  | { type: "SET_VARIABLES"; variables: PayrollVariable[] }
  | {
      type: "SET_EMPLOYEE_VARIABLES";
      employeeId: number;
      variable: PayrollVariable;
    }
  | {
      type: "UPDATE_EMPLOYEE_VARIABLES";
      employeeId: number;
      variable: Partial<PayrollVariable>;
    }
  | { type: "REMOVE_EMPLOYEE_VARIABLES"; employeeId: number }

  // Actions de calculs
  | { type: "SET_CALCULATIONS"; calculations: PayrollCalculation[] }
  | {
      type: "UPDATE_CALCULATION";
      employeeId: number;
      calculation: Partial<PayrollCalculation>;
    }
  | { type: "VALIDATE_CALCULATION"; employeeId: number }
  | { type: "VALIDATE_ALL_CALCULATIONS" }

  // Actions de lots
  | { type: "SET_CURRENT_BATCH"; batch: PayrollBatch }
  | { type: "UPDATE_BATCH"; batchId: number; batch: Partial<PayrollBatch> }

  // Actions de statistiques
  | { type: "SET_STATISTICS"; statistics: PayrollStatistics | null }

  // Actions d'erreur
  | { type: "ADD_ERROR"; error: PayrollError }
  | { type: "REMOVE_ERROR"; index: number }
  | { type: "CLEAR_ERRORS" }

  // Actions de configuration
  | { type: "UPDATE_CONFIG"; config: Partial<PayrollProcessingConfig> }

  // Actions globales
  | { type: "RESET" };

// ===== REDUCER =====

export const payrollReducer = (
  state: PayrollState,
  action: PayrollAction
): PayrollState => {
  switch (action.type) {
    // ===== CHARGEMENT =====
    case "SET_LOADING":
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.resource]: action.loading,
        },
      };

    // ===== PÉRIODES =====
    case "SET_PERIODS":
      return {
        ...state,
        periods: action.periods,
      };

    case "ADD_PERIOD":
      return {
        ...state,
        periods: [...state.periods, action.period],
      };

    case "UPDATE_PERIOD":
      return {
        ...state,
        periods: state.periods.map((period) =>
          period.id === action.id ? { ...period, ...action.period } : period
        ),
        currentPeriod:
          state.currentPeriod?.id === action.id
            ? { ...state.currentPeriod, ...action.period }
            : state.currentPeriod,
      };

    case "REMOVE_PERIOD":
      return {
        ...state,
        periods: state.periods.filter((period) => period.id !== action.id),
        currentPeriod:
          state.currentPeriod?.id === action.id ? null : state.currentPeriod,
      };

    case "SET_CURRENT_PERIOD":
      return {
        ...state,
        currentPeriod: action.period,
        // Réinitialiser les données liées à la période précédente
        employees: action.period ? state.employees : [],
        variables: action.period ? state.variables : [],
        calculations: action.period ? state.calculations : [],
        statistics: action.period ? state.statistics : null,
        currentBatch: action.period ? state.currentBatch : null,
      };

    // ===== EMPLOYÉS =====
    case "SET_EMPLOYEES":
      return {
        ...state,
        employees: action.employees,
      };

    case "ADD_EMPLOYEE":
      return {
        ...state,
        employees: [...state.employees, action.employee],
      };

    case "UPDATE_EMPLOYEE":
      return {
        ...state,
        employees: state.employees.map((employee) =>
          employee.id === action.id
            ? { ...employee, ...action.employee }
            : employee
        ),
      };

    case "REMOVE_EMPLOYEE":
      return {
        ...state,
        employees: state.employees.filter(
          (employee) => employee.employee_id !== action.employeeId
        ),
        // Supprimer aussi les variables et calculs associés
        variables: state.variables.filter(
          (variable) => variable.employee_id !== action.employeeId
        ),
        calculations: state.calculations.filter(
          (calculation) => calculation.employee_id !== action.employeeId
        ),
      };

    // ===== VARIABLES =====
    case "SET_VARIABLES":
      return {
        ...state,
        variables: action.variables,
        // Mettre à jour le statut des employés
        employees: state.employees.map((employee) => ({
          ...employee,
          has_variables: action.variables.some(
            (variable) => variable.employee_id === employee.id
          ),
        })),
      };

    case "SET_EMPLOYEE_VARIABLES":
      const existingVariableIndex = state.variables.findIndex(
        (variable) => variable.employee_id === action.employeeId
      );

      let updatedVariables: PayrollVariable[];
      if (existingVariableIndex >= 0) {
        // Mettre à jour les variables existantes
        updatedVariables = state.variables.map((variable, index) =>
          index === existingVariableIndex ? action.variable : variable
        );
      } else {
        // Ajouter de nouvelles variables
        updatedVariables = [...state.variables, action.variable];
      }

      return {
        ...state,
        variables: updatedVariables,
        employees: state.employees.map((employee) =>
          employee.id === action.employeeId
            ? { ...employee, has_variables: true }
            : employee
        ),
      };

    case "UPDATE_EMPLOYEE_VARIABLES":
      return {
        ...state,
        variables: state.variables.map((variable) =>
          variable.employee_id === action.employeeId
            ? { ...variable, ...action.variable }
            : variable
        ),
      };

    case "REMOVE_EMPLOYEE_VARIABLES":
      return {
        ...state,
        variables: state.variables.filter(
          (variable) => variable.employee_id !== action.employeeId
        ),
        employees: state.employees.map((employee) =>
          employee.id === action.employeeId
            ? { ...employee, has_variables: false }
            : employee
        ),
      };

    // ===== CALCULS =====
    case "SET_CALCULATIONS":
      return {
        ...state,
        calculations: action.calculations,
        employees: state.employees.map((employee) => ({
          ...employee,
          is_calculated: action.calculations.some(
            (calc) => calc.employee_id === employee.id
          ),
          is_validated: action.calculations.some(
            (calc) => calc.employee_id === employee.id && calc.is_validated
          ),
        })),
      };

    case "UPDATE_CALCULATION":
      return {
        ...state,
        calculations: state.calculations.map((calculation) =>
          calculation.employee_id === action.employeeId
            ? { ...calculation, ...action.calculation }
            : calculation
        ),
      };

    case "VALIDATE_CALCULATION":
      return {
        ...state,
        calculations: state.calculations.map((calculation) =>
          calculation.employee_id === action.employeeId
            ? {
                ...calculation,
                is_validated: true,
                validated_at: new Date().toISOString(),
              }
            : calculation
        ),
        employees: state.employees.map((employee) =>
          employee.id === action.employeeId
            ? { ...employee, is_validated: true }
            : employee
        ),
      };

    case "VALIDATE_ALL_CALCULATIONS":
      const now = new Date().toISOString();
      return {
        ...state,
        calculations: state.calculations.map((calculation) => ({
          ...calculation,
          is_validated: true,
          validated_at: now,
        })),
        employees: state.employees.map((employee) => ({
          ...employee,
          is_validated: true,
        })),
      };

    // ===== LOTS =====
    case "SET_CURRENT_BATCH":
      return {
        ...state,
        currentBatch: action.batch,
      };

    case "UPDATE_BATCH":
      return {
        ...state,
        currentBatch:
          state.currentBatch?.id === action.batchId
            ? { ...state.currentBatch, ...action.batch }
            : state.currentBatch,
      };

    // ===== STATISTIQUES =====
    case "SET_STATISTICS":
      return {
        ...state,
        statistics: action.statistics,
      };

    // ===== ERREURS =====
    case "ADD_ERROR":
      return {
        ...state,
        errors: [...state.errors, action.error],
      };

    case "REMOVE_ERROR":
      return {
        ...state,
        errors: state.errors.filter((_, index) => index !== action.index),
      };

    case "CLEAR_ERRORS":
      return {
        ...state,
        errors: [],
      };

    // ===== CONFIGURATION =====
    case "UPDATE_CONFIG":
      return {
        ...state,
        config: {
          ...state.config,
          ...action.config,
        },
      };

    // ===== RESET =====
    case "RESET":
      return {
        ...state,
        currentPeriod: null,
        currentBatch: null,
        employees: [],
        variables: [],
        calculations: [],
        statistics: null,
        errors: [],
        loading: {
          periods: false,
          employees: false,
          variables: false,
          calculations: false,
          batch: false,
          statistics: false,
        },
      };

    default:
      return state;
  }
};

// ===== SÉLECTEURS =====

export const payrollSelectors = {
  // Période courante
  getCurrentPeriod: (state: PayrollState) => state.currentPeriod,
  getCurrentBatch: (state: PayrollState) => state.currentBatch,

  // Employés avec enrichissement
  getEmployeesWithStatus: (state: PayrollState) =>
    state.employees.map((employee) => {
      const hasVariables = state.variables.some(
        (v) => v.employee_id === employee.id
      );
      const calculation = state.calculations.find(
        (c) => c.employee_id === employee.id
      );

      return {
        ...employee,
        has_variables: hasVariables,
        is_calculated: !!calculation,
        is_validated: calculation?.is_validated || false,
        net_salary: calculation?.net_salary || 0,
      };
    }),

  // Variables par employé
  getEmployeeVariables: (state: PayrollState, employeeId: number) =>
    state.variables.find((v) => v.employee_id === employeeId),

  // Calculs par employé
  getEmployeeCalculation: (state: PayrollState, employeeId: number) =>
    state.calculations.find((c) => c.employee_id === employeeId),

  // Statistiques calculées
  getProgressStats: (state: PayrollState) => {
    const total = state.employees.length;
    const withVariables = state.employees.filter((e) => e.has_variables).length;
    const calculated = state.employees.filter((e) => e.is_calculated).length;
    const validated = state.employees.filter((e) => e.is_validated).length;

    return {
      total,
      withVariables,
      calculated,
      validated,
      progressVariables: total > 0 ? (withVariables / total) * 100 : 0,
      progressCalculated: total > 0 ? (calculated / total) * 100 : 0,
      progressValidated: total > 0 ? (validated / total) * 100 : 0,
    };
  },

  // Erreurs par type
  getErrorsByType: (state: PayrollState) => {
    return state.errors.reduce((acc, error) => {
      acc[error.error_type] = (acc[error.error_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  },

  // État de chargement global
  isLoading: (state: PayrollState) =>
    Object.values(state.loading).some((loading) => loading),

  // Peut-on passer à l'étape suivante ?
  canProceedToCalculation: (state: PayrollState) =>
    state.employees.length > 0 && state.employees.every((e) => e.has_variables),

  canProceedToValidation: (state: PayrollState) =>
    state.calculations.length > 0 &&
    state.calculations.every((c) => c.calculated_at),

  canProceedToPayment: (state: PayrollState) =>
    state.calculations.length > 0 &&
    state.calculations.every((c) => c.is_validated),
};
